import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import CITIES from './allCityData';
import './PublicPages.css';

function CityPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const city = CITIES[citySlug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [citySlug]);

  if (!city) {
    return (
      <div className="public-page">
        <div className="content-section">
          <h1>City Not Found</h1>
          <p>Sorry, we don't have information for this city yet.</p>
          <Link to="/" className="btn btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  const handleNavigate = (path) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  const nearbyCities = (city.nearby || []).map(slug => CITIES[slug] ? { slug, ...CITIES[slug] } : null).filter(Boolean);

  return (
    <div className="public-page">
      <SEO
        title={`Best Restaurants in ${city.name}, ${city.state} — ${city.tagline || 'Swipe to Decide'}`}
        description={`Can't decide where to eat in ${city.name}? ${city.foodScene ? city.foodScene.slice(0, 120) + '...' : ''} Hangrily helps you swipe through ${city.restaurants} restaurants. Try free!`}
        path={`/city/${citySlug}`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `Restaurant Picker for ${city.name}, ${city.state}`,
            description: `Find the best restaurants in ${city.name} using Hangrily. ${city.knownFor || ''}`,
            url: `https://hangrily.app/city/${citySlug}`,
            about: { '@type': 'City', name: city.name, containedInPlace: { '@type': 'State', name: city.state } }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Locations', item: 'https://hangrily.app/locations' },
              { '@type': 'ListItem', position: 3, name: `${city.name}, ${city.state}`, item: `https://hangrily.app/city/${citySlug}` }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: `What is the best restaurant picker app for ${city.name}?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `Hangrily is the best restaurant picker for ${city.name}. It uses GPS to find ${city.restaurants} restaurants near you, lets you filter by cuisine, price, and mood, then swipe through options. Works solo, with a partner, or with a group of up to 10.`
                }
              },
              {
                '@type': 'Question',
                name: `How do I decide where to eat in ${city.name} with a group?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `Open Hangrily in ${city.name} and start a Group Session. Share the code with up to 10 friends. Everyone swipes through ${city.name} restaurants independently. The app finds the restaurant with the most group agreement — no more arguing or one person picking for everyone.`
                }
              },
              {
                '@type': 'Question',
                name: `What food is ${city.name} known for?`,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `${city.name} is known for ${city.knownFor || 'a diverse food scene'}. Use Hangrily to explore all of these cuisines and more — filter by what you are craving and swipe to decide.`
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="page-hero">
        <h1>Where to Eat in {city.name}, {city.state}</h1>
        <p className="hero-tagline" style={{ fontSize: '20px', fontStyle: 'italic', opacity: 0.9, marginBottom: '16px' }}>
          {city.tagline}
        </p>
        <p className="hero-subtitle">
          {city.restaurants} restaurants. {city.population} people with opinions. One app to end the argument.
        </p>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '32px', flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-lg" onClick={() => handleNavigate('/try')}>
            Try Hangrily in {city.name}
          </button>
          <button className="btn btn-secondary btn-lg" onClick={() => handleNavigate('/how-it-works')}>
            How It Works
          </button>
        </div>
      </section>

      {/* The Food Scene */}
      {city.foodScene && (
        <section className="content-section">
          <h2 className="section-title">The {city.name} Food Scene</h2>
          <p style={{ fontSize: '17px', lineHeight: 1.8, maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            {city.foodScene}
          </p>
        </section>
      )}

      {/* What This City Is Famous For */}
      {city.topCuisines && city.topCuisines.length > 0 && (
        <section className="content-section">
          <h2 className="section-title">What to Eat in {city.name}</h2>
          <p className="section-subtitle">The cuisines that define {city.name}'s food identity</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginTop: '40px' }}>
            {city.topCuisines.map((cuisine, i) => (
              <div key={i} className="mode-detail-card" style={{ padding: '28px', textAlign: 'center' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>{cuisine.emoji}</div>
                <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>{cuisine.name}</h3>
                <p style={{ fontSize: '14px', margin: 0, opacity: 0.8 }}>{cuisine.note}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Neighborhoods */}
      {city.neighborhoods && city.neighborhoods.length > 0 && (
        <section className="content-section">
          <h2 className="section-title">Best Food Neighborhoods in {city.name}</h2>
          <p className="section-subtitle">Where the locals actually eat</p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', marginTop: '32px' }}>
            {city.neighborhoods.map((hood, i) => (
              <span key={i} style={{
                background: 'rgba(255, 99, 71, 0.1)',
                border: '1px solid rgba(255, 99, 71, 0.3)',
                borderRadius: '24px',
                padding: '10px 20px',
                fontSize: '15px',
                fontWeight: 500
              }}>
                📍 {hood}
              </span>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '15px', opacity: 0.7 }}>
            Open Hangrily near any of these neighborhoods to find restaurants within walking distance.
          </p>
        </section>
      )}

      {/* How It Works in This City */}
      <section className="content-section">
        <h2 className="section-title">How Hangrily Works in {city.name}</h2>

        <div className="process-flow">
          <div className="process-step">
            <div className="step-icon">📍</div>
            <div className="step-details">
              <span className="step-badge">Step 1</span>
              <h2>Drop a Pin in {city.name}</h2>
              <p>Allow location access or search for a {city.name} neighborhood. Hangrily pulls live restaurant data from Google Places — {city.restaurants} options and counting.</p>
            </div>
          </div>

          <div className="process-step">
            <div className="step-icon">🎛️</div>
            <div className="step-details">
              <span className="step-badge">Step 2</span>
              <h2>Filter for What You Want</h2>
              <p>Craving {city.topCuisines && city.topCuisines[0] ? city.topCuisines[0].name : 'something specific'}? Set cuisine, price range, distance, and mood. {city.name} has everything — narrow it down to what sounds good right now.</p>
            </div>
          </div>

          <div className="process-step">
            <div className="step-icon">👆</div>
            <div className="step-details">
              <span className="step-badge">Step 3</span>
              <h2>Swipe Solo or Together</h2>
              <p>Flying solo? Just swipe. Date night? Both of you swipe independently. Group outing in {city.name}? Up to 10 friends join and swipe on their own phones.</p>
            </div>
          </div>

          <div className="process-step">
            <div className="step-icon">🎉</div>
            <div className="step-details">
              <span className="step-badge">Step 4</span>
              <h2>Get Your {city.name} Match</h2>
              <p>The restaurant with the most agreement wins. Get the address, see the rating, and go eat. Decision made in under 2 minutes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Known For */}
      {city.knownFor && (
        <section className="content-section" style={{ textAlign: 'center' }}>
          <h2 className="section-title">{city.name} Is Known For</h2>
          <p style={{ fontSize: '18px', lineHeight: 1.8, maxWidth: '700px', margin: '16px auto 0' }}>
            {city.knownFor}. All of these are searchable and swipeable in Hangrily — just set your preferences and start discovering.
          </p>
        </section>
      )}

      {/* Nearby Cities */}
      {nearbyCities.length > 0 && (
        <section className="content-section">
          <h2 className="section-title">Also Nearby</h2>
          <p className="section-subtitle">Explore restaurants in cities near {city.name}</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginTop: '32px' }}>
            {nearbyCities.map(nearby => (
              <Link
                key={nearby.slug}
                to={`/city/${nearby.slug}`}
                className="mode-detail-card"
                style={{ padding: '24px', textAlign: 'center', cursor: 'pointer', textDecoration: 'none' }}
                onClick={() => window.scrollTo(0, 0)}
              >
                <h3 style={{ fontSize: '18px', marginBottom: '6px' }}>{nearby.name}, {nearby.state}</h3>
                <p style={{ fontSize: '14px', margin: 0, opacity: 0.7 }}>{nearby.restaurants} restaurants • {nearby.tagline}</p>
              </Link>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Link to="/locations" className="btn btn-secondary" onClick={() => window.scrollTo(0, 0)}>
              View All 54 Cities
            </Link>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="content-section alt-bg">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <div className="faq-list">
          <details className="faq-item">
            <summary>What is the best restaurant picker app for {city.name}?</summary>
            <p>Hangrily is the best restaurant picker for {city.name}. It uses GPS to find {city.restaurants} restaurants near you, lets you filter by cuisine, price, and mood, then swipe through options. Works solo, with a partner, or with a group of up to 10.</p>
          </details>
          <details className="faq-item">
            <summary>How do I decide where to eat in {city.name} with a group?</summary>
            <p>Open Hangrily in {city.name} and start a Group Session. Share the code with up to 10 friends. Everyone swipes through {city.name} restaurants independently. The app finds the restaurant with the most group agreement — no more arguing or one person picking for everyone.</p>
          </details>
          <details className="faq-item">
            <summary>What food is {city.name} known for?</summary>
            <p>{city.name} is known for {city.knownFor || 'a diverse food scene'}. Use Hangrily to explore all of these cuisines and more — filter by what you are craving and swipe to decide.</p>
          </details>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="cta-content">
          <h2>Stop Arguing About Where to Eat in {city.name}</h2>
          <p>{city.restaurants} restaurants. Your crew. One swipe-based decision. Try it free.</p>
          <button className="btn" onClick={() => handleNavigate('/try')}>
            Get Started in {city.name}
          </button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

export default CityPage;
export { CITIES };
